import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Clock, Inbox } from "lucide-react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import AdminStatCard from "components/admin/AdminStatCard";
import StatusBadge from "components/admin/StatusBadge";
import Pagination from "components/Pagination";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import {
  AdminChamado,
  ChamadoStatus,
  ChamadoTipo,
  listAdminChamados,
} from "services/admin/adminChamados.service";

const SELECT_CLASS = "h-9 rounded-md border border-input bg-background px-3 text-sm";

export const ROTULO_STATUS: Record<ChamadoStatus, string> = {
  EM_ANALISE: "Com a Intrex",
  ACAO_CLIENTE: "Com o cliente",
  RESOLVIDO: "Resolvido",
};

export const TOM_STATUS: Record<ChamadoStatus, "red" | "yellow" | "green"> = {
  // Vermelho no que espera a Intrex: é a fila que o atendimento precisa zerar.
  EM_ANALISE: "red",
  ACAO_CLIENTE: "yellow",
  RESOLVIDO: "green",
};

export const ROTULO_TIPO: Record<string, string> = {
  ALFANDEGA: "Alfândega",
  ENDERECO: "Endereço",
  ATRASO: "Atraso",
  TENTATIVA_ENTREGA: "Tentativa de entrega",
  AVARIA: "Avaria",
  OUTRO: "Outro",
};

const dataCurta = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

/** Dias desde a abertura: chamado parado é o que precisa aparecer. */
const diasParado = (iso: string) =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

const AdminChamados = () => {
  const navigate = useNavigate();
  const [chamados, setChamados] = useState<AdminChamado[]>([]);
  const [resumo, setResumo] = useState({ abertos: 0, aguardando_intrex: 0, RESOLVIDO: 0 });
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState<ChamadoStatus | "">("");
  const [tipo, setTipo] = useState<ChamadoTipo | "">("");
  const [q, setQ] = useState("");
  // Abre já filtrado nos abertos: resolvido não é trabalho pendente.
  const [somenteAbertos, setSomenteAbertos] = useState(true);

  useEffect(() => {
    setLoading(true);
    setErro(null);
    listAdminChamados({
      page,
      pageSize: 25,
      status,
      tipo,
      q,
      abertos: somenteAbertos && !status ? "1" : "",
    })
      .then((resp) => {
        setChamados(resp.chamados);
        setResumo({
          abertos: resp.resumo.abertos,
          aguardando_intrex: resp.resumo.aguardando_intrex,
          RESOLVIDO: resp.resumo.RESOLVIDO,
        });
        setTotalPages(Math.max(1, Math.ceil(resp.total / resp.pageSize)));
      })
      .catch((err) => {
        console.error("[AdminChamados] erro:", err);
        setErro(
          err?.response?.data?.error ||
            "Não foi possível carregar os chamados. Confira se o backend está publicado."
        );
      })
      .finally(() => setLoading(false));
  }, [page, status, tipo, q, somenteAbertos]);

  return (
    <AdminLayout title="Chamados" subtitle="Ocorrências dos envios, por cliente">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <AdminStatCard
            label="Aguardando a Intrex"
            value={resumo.aguardando_intrex}
            icon={<AlertCircle />}
            tone={resumo.aguardando_intrex > 0 ? "danger" : "default"}
          />
          <AdminStatCard label="Abertos" value={resumo.abertos} icon={<Inbox />} />
          <AdminStatCard label="Resolvidos" value={resumo.RESOLVIDO} icon={<Clock />} tone="success" />
        </div>

        <AdminCard noPadding className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Buscar no título..."
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              className="max-w-xs"
            />
            <select
              className={SELECT_CLASS}
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value as ChamadoStatus | "");
              }}
            >
              <option value="">Todos os status</option>
              <option value="EM_ANALISE">Com a Intrex</option>
              <option value="ACAO_CLIENTE">Com o cliente</option>
              <option value="RESOLVIDO">Resolvido</option>
            </select>
            <select
              className={SELECT_CLASS}
              value={tipo}
              onChange={(e) => {
                setPage(1);
                setTipo(e.target.value as ChamadoTipo | "");
              }}
            >
              <option value="">Todos os tipos</option>
              {Object.entries(ROTULO_TIPO).map(([valor, rotulo]) => (
                <option key={valor} value={valor}>
                  {rotulo}
                </option>
              ))}
            </select>
            {!status && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={somenteAbertos}
                  onChange={(e) => {
                    setPage(1);
                    setSomenteAbertos(e.target.checked);
                  }}
                />
                Só abertos
              </label>
            )}
          </div>
        </AdminCard>

        <AdminCard noPadding className="p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : erro ? (
            <p className="text-sm text-red-600">{erro}</p>
          ) : chamados.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum chamado com esses filtros.</p>
          ) : (
            <>
              {/* Tabela à mão, e não CustomTable: cada linha tem badge, dois
                  níveis de texto e um botão — CustomTable só renderiza células
                  simples a partir de um objeto plano. */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4 font-semibold">Chamado</th>
                      <th className="py-2 pr-4 font-semibold">Cliente</th>
                      <th className="py-2 pr-4 font-semibold">Envio</th>
                      <th className="py-2 pr-4 font-semibold">Status</th>
                      <th className="py-2 pr-4 font-semibold">Aberto</th>
                      <th className="py-2 font-semibold" />
                    </tr>
                  </thead>
                  <tbody>
                    {chamados.map((c) => {
                      const dias = diasParado(c.criado_em);
                      return (
                        <tr key={c.id} className="border-t border-slate-100 align-top">
                          <td className="py-3 pr-4 max-w-[320px]">
                            <p className="font-medium text-slate-900">{c.titulo}</p>
                            <p className="text-[12px] text-slate-500">
                              #{c.id} · {ROTULO_TIPO[c.tipo] ?? c.tipo}
                              {c.origem === "CLIENTE" ? " · relatado pelo cliente" : ""}
                            </p>
                          </td>
                          <td className="py-3 pr-4">
                            <p className="text-slate-800">{c.cliente?.razaoSocial ?? "—"}</p>
                            <p className="text-[12px] text-slate-500">
                              {c.cliente?.emailPrincipal ?? ""}
                            </p>
                          </td>
                          <td className="py-3 pr-4">
                            {c.envio ? (
                              <>
                                <p className="text-slate-800">{c.envio.pedido_ref}</p>
                                <p className="text-[12px] text-slate-500">
                                  {c.envio.carrier}
                                  {c.envio.destino ? ` · ${c.envio.destino}` : ""}
                                </p>
                              </>
                            ) : (
                              <span className="text-slate-400">sem envio</span>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            <StatusBadge
                              label={ROTULO_STATUS[c.status]}
                              tone={TOM_STATUS[c.status]}
                            />
                            {c.prazo && c.status !== "RESOLVIDO" ? (
                              <p className="text-[12px] text-slate-500 mt-1">
                                prazo {dataCurta(c.prazo)}
                              </p>
                            ) : null}
                          </td>
                          <td className="py-3 pr-4 whitespace-nowrap">
                            <p className="text-slate-800">{dataCurta(c.criado_em)}</p>
                            <p
                              className={
                                dias >= 3 && c.status === "EM_ANALISE"
                                  ? "text-[12px] text-red-600 font-medium"
                                  : "text-[12px] text-slate-500"
                              }
                            >
                              {dias === 0 ? "hoje" : `${dias}d`}
                            </p>
                          </td>
                          <td className="py-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/chamados/${c.id}`)}
                            >
                              Abrir
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default AdminChamados;
